"use client";
import React from "react";
import type { CTASection } from "@/types/sections/cta";
import {
  getBackgroundStyle,
  getBtnVariant,
  paddingMap,
} from "@/lib/background";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import Image from "next/image";

export default function CtaMultipleButtons({ data }: CTASection) {
  const {
    title,
    subtitle,
    buttons = [],
    background,
    alignment = "center",
    padding = "xl",
  } = data || {};

  const bgStyle = getBackgroundStyle(background);

  const alignMap = {
    center: "text-center justify-center",
    left: "text-left justify-start",
    right: "text-right justify-end",
  };

  return (
    <section
      className={`${paddingMap[padding]} ${alignMap[alignment]?.split(" ")[0]} relative`}
      style={bgStyle}
    >
      {background?.type === "image" && background?.value && (
        <Image
          src={background.value}
          alt={title || subtitle || ""}
          fill
          priority
          className="object-cover object-center z-0"
        />
      )}
      <div className="max-w-4xl mx-auto relative z-10">
        {title && (
          <h2 className="text-3xl font-bold mb-2 text-foreground">{title}</h2>
        )}
        {subtitle && (
          <p className="text-lg text-muted-foreground mb-5">{subtitle}</p>
        )}

        <div
          className={`flex flex-wrap gap-4 ${alignMap[alignment] || "justify-center"}`}
        >
          {buttons.map((btn, i) => {
            const isExternal = btn.href?.startsWith("http");

            return isExternal ? (
              <a
                key={i}
                href={btn.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  asChild
                  variant={getBtnVariant(btn.style)}
                  className="px-5 py-2 text-sm"
                >
                  <span>{btn.text}</span>
                </Button>
              </a>
            ) : (
              <Link key={i} href={btn.href}>
                <Button asChild variant={getBtnVariant(btn.style)}>
                  <span>{btn.text}</span>
                </Button>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
